/**
 * The manager pages' shared frame (manager redesign, 2026-10-09): one page header with its actions,
 * local tabs, quiet notices, a closed explanation, and a detail panel that sits beside the list and
 * becomes a sheet on a phone. Styles in page.css, scoped to these classes so Vaktplan is untouched.
 */
import { T } from '@/i18n';
import { Icon, type IconName } from '@/ui/icons';
import { type KeyboardEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import './page.css';

export function PageHead({
  title,
  sub,
  actions,
  icon,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** the page's own icon before its title, the one its menu item shows (#154 BR-01); decorative */
  icon?: IconName;
  /** the local tabs, under the title */
  children?: ReactNode;
}) {
  return (
    <header className="mh">
      <div className="mhrow">
        <div className="mht">
          <h1 tabIndex={-1}>
            {icon && (
              <span className="mhi">
                <Icon name={icon} />
              </span>
            )}
            {title}
          </h1>
          {sub != null && <p className="mhs">{sub}</p>}
        </div>
        {actions != null && <div className="mha">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export interface TabItem {
  key: string;
  label: ReactNode;
  /** a tab that is an address: the browser's Back and a bookmark land on it */
  to?: string;
  count?: number | null;
  /** an accessible name when the label alone is ambiguous, with the count in words */
  aria?: string;
  /** a 17 px outline icon before the label (#154 BR-12); decorative, the label stays */
  icon?: IconName;
}

/** The ids that tie a tab to its panel; TabPanel below derives the same pair (issue #82). */
const tabId = (key: string) => `tab-${key}`;
const panelId = (key: string) => `tabpanel-${key}`;

/**
 * The address of a tab just clicked to another page (issue #79). That click unmounts its tablist,
 * so the browser drops focus to the body; the tablist that mounts selected on this address takes
 * focus once. A plain link to the same page leaves it null, so nothing moves.
 */
let pending: string | null = null;

/**
 * Manager tabs: one tab stop, arrow keys, Home and End, focus kept on the chosen tab after it
 * renders (consistency fixes, 2026-10-09). A tab with `to` is a link, so the address names it.
 */
export function Tabs({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: TabItem[];
  value: string;
  onChange?: (key: string) => void;
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // a same-page tab keeps its element, so this finds it focused already and only clears the flag
  useEffect(() => {
    if (pending == null) return;
    const on = ref.current?.querySelector<HTMLElement>(`[id="${tabId(value)}"]`);
    if (on?.getAttribute('href') === pending) on.focus();
    pending = null;
  }, [value]);
  const move = (e: KeyboardEvent) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = items.findIndex((x) => x.key === value);
    const n = items.length;
    const j =
      e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    const el = ref.current?.querySelectorAll<HTMLElement>('[role=tab]')[j];
    el?.click();
    el?.focus();
  };
  return (
    <div
      className={`mtabs${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={label}
      ref={ref}
      onKeyDown={move}
    >
      {items.map((x) => {
        const on = x.key === value;
        return x.to ? (
          <NavLink
            key={x.key}
            id={tabId(x.key)}
            to={x.to}
            role="tab"
            aria-selected={on}
            aria-controls={panelId(x.key)}
            aria-label={x.aria}
            tabIndex={on ? 0 : -1}
            className={on ? 'on' : ''}
            onClick={() => {
              if (!on) pending = x.to ?? null;
            }}
            end
          >
            <TabBody label={x.label} count={x.count ?? null} icon={x.icon} />
          </NavLink>
        ) : (
          <button
            key={x.key}
            id={tabId(x.key)}
            type="button"
            role="tab"
            aria-selected={on}
            aria-controls={panelId(x.key)}
            aria-label={x.aria}
            tabIndex={on ? 0 : -1}
            className={on ? 'on' : ''}
            onClick={() => onChange?.(x.key)}
          >
            <TabBody label={x.label} count={x.count ?? null} icon={x.icon} />
          </button>
        );
      })}
    </div>
  );
}

/** The selected tab's content, named by its tab; one per page, under the PageHead (issue #82). */
export const TabPanel = ({ tab, children }: { tab: string; children: ReactNode }) => (
  <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
    {children}
  </div>
);

const TabBody = ({
  label,
  count,
  icon,
}: { label: ReactNode; count: number | null; icon?: IconName | undefined }) => (
  <>
    {icon && <Icon name={icon} />}
    {label}
    {count != null && count > 0 && <span className="mtc">{count}</span>}
  </>
);

/** A short closed explanation beside the thing it explains ("Hva teller som et punkt?"). */
export function Explain({ q, children, className }: { q: string; children: ReactNode; className?: string }) {
  return (
    <details className={`mexp${className ? ` ${className}` : ''}`}>
      <summary>{q}</summary>
      <div className="mexb">{children}</div>
    </details>
  );
}

export function Notice({
  tone = 'warn',
  title,
  children,
  action,
}: {
  tone?: 'warn' | 'dgr' | 'info' | 'ok';
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`mnote ${tone}`} role={tone === 'dgr' ? 'alert' : undefined}>
      <Icon name={tone === 'dgr' ? 'stop' : tone === 'warn' ? 'warn' : tone === 'ok' ? 'check' : 'info'} />
      <div className="mnt">
        <b>{title}</b>
        {children != null && <div>{children}</div>}
      </div>
      {action != null && <div className="mna">{action}</div>}
    </div>
  );
}

/**
 * The selected object's details beside the list (desktop) or as a sheet over it (phone). Escape and
 * the close button both go through onClose, which may refuse (an unsaved note asks first). Focus
 * moves into the panel on open and back to what opened it on close.
 */
/** Keeps Tab inside a panel: past the last control to the first, Shift+Tab before the first to the last. */
export function trapTab(
  e: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'preventDefault'>,
  box: HTMLElement,
): void {
  if (e.key !== 'Tab') return;
  const f = [
    ...box.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
    ),
    // a control hidden by CSS takes no focus: as the first stop, Tab would never leave the last (#272 A-S04)
  ].filter((x) => !x.closest('[hidden]') && (typeof x.checkVisibility !== 'function' || x.checkVisibility()));
  const first = f[0];
  const last = f[f.length - 1];
  const a = document.activeElement;
  if (e.shiftKey && (a === first || !box.contains(a) || a === box.querySelector('h2'))) {
    e.preventDefault();
    last?.focus();
  } else if (!e.shiftKey && a === last) {
    e.preventDefault();
    first?.focus();
  }
}

export function DetailPanel({
  title,
  sub,
  onClose,
  children,
  footer,
  label,
  restoreFocusTo,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  label?: string;
  /** Row action fallback for a panel opened from a direct URL instead of an in-page trigger. */
  restoreFocusTo?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);
  const returnTarget = useRef(restoreFocusTo);
  returnTarget.current = restoreFocusTo;
  useLayoutEffect(() => {
    opener.current = document.activeElement;
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
    // beside the list the whole panel, its actions too, comes into view (#272 K-09); a drawer is fixed anyway
    // only beside the list: a drawer is fixed over the page, and scrolling for it moved the page (#272 K-29)
    if (ref.current && getComputedStyle(ref.current).position !== 'fixed')
      ref.current.scrollIntoView?.({ block: 'nearest' });
    return () => {
      const back = opener.current as HTMLElement | null;
      // opened from the address (reload, Back, a link) the opener is the page: back to the named row action, else the
      // selected row (#272 K-18), else the heading
      if (back?.isConnected && back !== document.body && back !== document.documentElement) back.focus();
      else {
        const row =
          (returnTarget.current ? document.querySelector<HTMLElement>(returnTarget.current) : null) ??
          document.querySelector<HTMLElement>('tr.sel button, tr.sel a, .sel button');
        (row ?? document.querySelector<HTMLElement>('#main h1'))?.focus();
      }
    };
  }, []);
  /** Tab stays inside the panel, beside the list too: it used to walk out into the page after the last
   *  action and Escape then had nothing to close (#272 K-06, A-R09) */
  const trap = (e: React.KeyboardEvent) => ref.current && trapTab(e, ref.current);
  return (
    <>
      {/* the scrim of the drawer at tablet and phone widths: the page behind takes no taps, a tap closes */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes the panel from the keyboard; the scrim is a pointer target only */}
      <div className="mscrim" aria-hidden="true" onClick={onClose} />
      <aside
        className="mpanel"
        ref={ref}
        aria-label={label}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          } else trap(e);
        }}
      >
        <div className="mph">
          <div>
            <h2 tabIndex={-1}>{title}</h2>
            {sub != null && <p>{sub}</p>}
          </div>
          <button type="button" className="mpx" aria-label={T('Lukk')} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="mpb">{children}</div>
        {footer != null && <div className="mpf">{footer}</div>}
      </aside>
    </>
  );
}

/** A label and value row in a detail panel. */
export const Fact = ({ k, children }: { k: ReactNode; children: ReactNode }) => (
  <div className="mfact">
    <span>{k}</span>
    <b>{children}</b>
  </div>
);

/** Initials for a person's avatar. */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** A status label in a row: a quiet filled tag, the words carry the meaning, the colour supports it. */
export const Status = ({
  tone,
  icon,
  children,
}: {
  tone: 'ok' | 'warn' | 'dgr' | 'neu' | 'act';
  /** the tone's icon unless named: a wait is a clock, not a warning (Fravær V1.4, #276) */
  icon?: IconName;
  children: ReactNode;
}) => (
  <span className={`mstat ${tone}`}>
    {(icon || tone === 'warn' || tone === 'dgr' || tone === 'ok') && (
      <Icon name={icon ?? (tone === 'dgr' ? 'stop' : tone === 'ok' ? 'check' : 'warn')} />
    )}
    <span>{children}</span>
  </span>
);
